import {useState} from 'react';

import {BaseCondition} from 'studies/typed/shared/useExperimentDesignState';

import {
  buildConditionGroupsFromTableUpdate,
  buildConditionsFromTableUpdate,
  dispatchByConditionShape,
} from './replicateTableUtils';

interface UseConditionDescriptionDraftParams<TCondition extends BaseCondition> {
  conditions: Array<TCondition>;
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void;
}

interface UseConditionDescriptionDraftResult {
  isEditDescriptionModalOpen: boolean;
  descriptionDraft: string;
  setDescriptionDraft: (descriptionDraft: string) => void;
  openEditDescriptionModal: (conditionIndex: number) => void;
  closeEditDescriptionModal: () => void;
  saveDescriptionDraft: () => void;
}

export function useConditionDescriptionDraft<TCondition extends BaseCondition>({
  conditions,
  setConditions,
}: UseConditionDescriptionDraftParams<TCondition>): UseConditionDescriptionDraftResult {
  const [isEditDescriptionModalOpen, setIsEditDescriptionModalOpen] = useState(false);
  const [descriptionConditionIndex, setDescriptionConditionIndex] = useState<number | null>(null);
  const [descriptionDraft, setDescriptionDraft] = useState('');

  const openEditDescriptionModal = (conditionIndex: number): void => {
    setDescriptionConditionIndex(conditionIndex);
    setDescriptionDraft(conditions[conditionIndex]?.description ?? '');
    setIsEditDescriptionModalOpen(true);
  };

  const closeEditDescriptionModal = (): void => {
    setIsEditDescriptionModalOpen(false);
    setDescriptionConditionIndex(null);
    setDescriptionDraft('');
  };

  const saveDescriptionDraft = (): void => {
    if (descriptionConditionIndex == null) {
      return;
    }
    const newConditions = conditions.map((condition, index) => ({
      name: condition.name,
      numReplicates: condition.numReplicates,
      description: index === descriptionConditionIndex ? descriptionDraft : condition.description,
    }));
    const originalValueIndexes = conditions.map((_condition, index) => index);
    const updated = dispatchByConditionShape(
      conditions,
      (cs) => buildConditionsFromTableUpdate({newConditions, originalValueIndexes, conditions: cs}),
      (cs) => buildConditionGroupsFromTableUpdate({newConditions, originalValueIndexes, conditions: cs})
    );
    setConditions(updated as unknown as Array<TCondition>, originalValueIndexes);
    closeEditDescriptionModal();
  };

  return {
    isEditDescriptionModalOpen,
    descriptionDraft,
    setDescriptionDraft,
    openEditDescriptionModal,
    closeEditDescriptionModal,
    saveDescriptionDraft,
  };
}
